import React, { memo, useCallback, useEffect, useRef, useState, type JSX } from 'react';
import type { ChangeEvent, MouseEvent, SyntheticEvent } from 'react';
import { useSelector } from 'react-redux';

import Check from '@mui/icons-material/Check';
import DeviceHubIcon from '@mui/icons-material/DeviceHub';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import MenuButton from '@mui/icons-material/MoreVert';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';

import type { RecordOf } from 'immutable';
import { List, Record } from 'immutable';

import { useAppSelector, useAppStore } from 'hooks/redux';
import usePrivacyMaskValue from 'app-components/hooks/privacyMode/usePrivacyMaskValue';

import { accountsSelectors } from 'app-components/flux/accounts';
import { chartOfAccountsTypes } from 'app-components/flux/chart-of-accounts';
import { categoriesSelectors } from 'app-components/flux/categories';
import { entitlementsSelectors } from 'app-components/flux/entitlements';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import {
  SPLITS_ALWAYS_SHOW_FIELD_LABELS,
  SPLITS_ALWAYS_SHOW_ORDER,
  transactionsSelectors,
  transactionsTypes,
  transactionsUtils,
  useSplitsAlwaysShow,
} from 'app-components/flux/transactions';
import type { SplitItemProps, SplitProps, TransactionProps } from 'app-components/flux/transactions/transactionsTypes';
import { tracker } from 'app-components/utils/core';

import StdDialog from 'components/Dialogs/StdDialog';
import StdDialogTitle from 'components/Dialogs/StdDialogTitle';
import QAmountField from 'components/QAmountField';
import QButton from 'components/QButton';

import type { OrnamentType, ProjectSelect } from 'components/ClientProjectSelect';
import { getOrnamentType } from 'components/ClientProjectSelect';

import { getTotalNumberOfSplitsWithAllocations } from 'data/transactionList/selectors';

import {
  createLeftToSplit,
  getInitialSplitState,
  isOverflowAmountEditable,
  resolveSplitItemExclusions,
  splitItemsToArray,
  splitItemsToArrayOverflowLast,
  sumNonOverflowSplitItems,
  validateSplitDialogState,
  createSplitToPersist,
  isOverflowSplitItem,
  splitEvenlyUtil,
  ensureOverflowSplitIsLast,
  createLeftToSplitLine,
  addEmptySplitLine,
  getFirstNonOverflowSplitItem,
  getSplitQbpLinePolicy,
  getInsertedEditableSplitIndex,
  isLegacyNACoa,
  type SplitQbpLinePolicy,
  normalizeSplits,
  type SplitFieldDefaults,
} from 'app-components/flux/transactions/splitsHelpers';

import { getStandardDrawerFloatingSx, useStandardDrawerLayerContainer } from 'components/Drawers/StandardDrawer';
import type { RootModalDrawerOptions } from 'components/Drawers/standardDrawerContract';
import type { SplitDialogMode } from 'components/Transactions/SplitDialog/actions';
import SplitRow from 'components/Transactions/SplitDialog/SplitRow';
import { useStyles } from 'components/Transactions/SplitDialog/styles';
import Tooltip from '@mui/material/Tooltip';
import StdDialogActions from 'components/Dialogs/StdDialogActions';
import StdDialogContent from 'components/Dialogs/StdDialogContent';
import {
  getTxnTransferHasAllocations,
  isArAssociatedPaymentTxn,
} from 'components/Dialogs/TransactionEditDialog/selectors';
import { getLength } from 'app-components/utils/core/immutableHelpers';
import { formatSplitItemName } from 'app-components/flux/transactions/splitsHelpers';

type SplitDialogHeaderProps = {
  mode: SplitDialogMode;
  payee: string | null | undefined;
};

type SplitDialogShellProps = {
  children: React.ReactNode;
  dialogProps: { readonly [key: string]: unknown };
  isDrawer: boolean;
  onBack: () => void;
  onClose: () => void;
  open: boolean;
  paperClassName: string;
  title: string;
};

function SplitDialogShell({
  children,
  dialogProps,
  isDrawer,
  onBack,
  onClose,
  open,
  paperClassName,
  title,
}: SplitDialogShellProps): JSX.Element | null {
  if (!open) {
    return null;
  }

  if (isDrawer) {
    return (
      <Box
        aria-label={title}
        role='dialog'
        sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}
      >
        <StdDialogTitle
          variant='drawer'
          id='dialog-title'
          onBack={onBack}
          onClose={onClose}
          showBackButton
          showCloseButton
        >
          {title}
        </StdDialogTitle>
        {children}
      </Box>
    );
  }

  return (
    <StdDialog
      size='md'
      showBackButton
      disableBackdropClick
      disableEscapeKeyDown
      title={title}
      onBack={onBack}
      open={open}
      {...dialogProps}
      paperProps={{
        className: paperClassName,
        sx: {
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
    >
      {children}
    </StdDialog>
  );
}

type SplitVisibleSections = {
  clientProject: boolean;
  tags: boolean;
  exclusions: boolean;
  note: boolean;
};

const SPLITS_ALWAYS_SHOW_MENU_ITEMS: ReadonlyArray<{
  key: keyof SplitFieldDefaults;
  label: string;
  id: string;
}> = SPLITS_ALWAYS_SHOW_ORDER.map((key) => ({
  key,
  label: SPLITS_ALWAYS_SHOW_FIELD_LABELS[key],
  id: `menu-splits-always-${key}`,
}));

/** Focus target for a row section after it becomes visible. */
const getSplitSectionFocusTarget = (index: number, section: keyof SplitVisibleSections): HTMLElement | null => {
  switch (section) {
    case 'note':
      return document.getElementById(`split-note-${index}-input`);
    case 'exclusions':
      return document.getElementById(`split-exclusions-${index}-trigger`);
    case 'tags': {
      const byId = document.getElementById(`split-tags-${index}`);
      if (byId instanceof HTMLInputElement || byId instanceof HTMLTextAreaElement) {
        return byId;
      }
      return (
        (byId?.querySelector('input.MuiAutocomplete-input') as HTMLElement | null) ??
        (byId?.querySelector('input[role="combobox"]') as HTMLElement | null) ??
        byId
      );
    }
    case 'clientProject': {
      const wrap = document.getElementById(`split-client-project-${index}-section`);
      const input =
        (wrap?.querySelector('input.MuiAutocomplete-input') as HTMLElement | null) ??
        (wrap?.querySelector('input[role="combobox"]') as HTMLElement | null);
      return input ?? wrap;
    }
    default:
      return null;
  }
};

/** Scroll the full split row block (header + fields) into the visible scroll pane. */
const scrollSeriesSplitSectionIntoView = (index: number): void => {
  const el = document.getElementById(`split-series-section-${index}`);
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
};

const getAllowedSplitOrnamentType = (ornamentType: OrnamentType, policy: SplitQbpLinePolicy): OrnamentType | 'none' => {
  if (ornamentType === 'billable') {
    return 'billable';
  }

  return policy.allowsPaymentOrnaments ? ornamentType : 'none';
};

type Props = {
  open: boolean;
  onClose: () => void;
  onBack: () => void;
  title?: string;
  txn: TransactionProps;
  setSplit: (val: RecordOf<SplitProps> | null) => void;
  setAmount: (val: number) => void;
  setCoa: (newCOA?: chartOfAccountsTypes.ChartOfAccountProps | null) => void;
  setTags: (val: TransactionProps['tags']) => void;
  accountId: string;
  disabledPaymentFn: (bId: string) => boolean;
  setCustomersAndProjects: React.Dispatch<React.SetStateAction<ProjectSelect>>;
  mode?: SplitDialogMode;
  readOnly?: boolean;
  rootModalDrawer?: RootModalDrawerOptions;
};

const stopProp = (e: SyntheticEvent): void => {
  e.stopPropagation();
};

const SplitDialog = (props: Props): JSX.Element => {
  const { classes } = useStyles();
  const {
    accountId,
    title,
    txn,
    open,
    setSplit,
    setCoa,
    setTags,
    onBack,
    setAmount,
    setCustomersAndProjects,
    disabledPaymentFn,
    mode: modeProp = 'transaction',
    readOnly = false,
    rootModalDrawer,
    ...dialogProps
  } = props;

  const splitSeriesFf = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'splitSeries'));
  const mode: SplitDialogMode = modeProp === 'series' && !splitSeriesFf ? 'transaction' : modeProp;
  const isReadOnlyMode = Boolean(readOnly);
  const isSeriesMode = mode === 'series';
  const usesNewSplitLineUi = mode === 'series' || mode === 'reminder' || mode === 'transaction';
  const isOverflowMode = mode === 'series' || mode === 'reminder';
  const businessEnabled = useAppSelector(entitlementsSelectors.getBusinessEnabled);
  const appStore = useAppStore();
  const {
    enabled: splitsAlwaysShowEnabled,
    defaults: splitsAlwaysShow,
    commit: commitSplitsAlwaysShow,
  } = useSplitsAlwaysShow();
  const arAccountIds = useAppSelector(accountsSelectors.getAllArAccountIds);
  const defaultTitle =
    mode === 'series' ? 'Assign Splits to Series' : mode === 'reminder' ? 'Split Reminder' : 'Split Transaction';

  const seedSplitState = useCallback(
    (splitState: RecordOf<SplitProps>): RecordOf<SplitProps> => {
      const splitItems = splitItemsToArray(splitState.items);
      const seededItems = splitItems.map((item) => ({
        ...item,
        ...resolveSplitItemExclusions({
          splitItem: item,
          mode,
          state: appStore.getState(),
          phase: 'seed',
        }),
      }));

      return splitState.set('items', transactionsTypes.mkSplitItems(seededItems)) as RecordOf<SplitProps>;
    },
    [appStore, mode],
  );

  const computeInitialSplitState = useCallback((): RecordOf<SplitProps> => {
    const splitRecord =
      txn.split == null
        ? null
        : Record.isRecord(txn.split)
          ? (txn.split as RecordOf<SplitProps>)
          : (transactionsTypes.mkSplit(txn.split as SplitProps) as RecordOf<SplitProps>);

    const initialState = getInitialSplitState({
      txn,
      split: splitRecord,
      isOverflowMode,
    });

    return seedSplitState(initialState);
  }, [txn, isOverflowMode, seedSplitState]);

  const initialSplitStateForMountRef = useRef<RecordOf<SplitProps> | null>(null);
  const initialSplitStateForMount = (() => {
    const existing = initialSplitStateForMountRef.current;
    if (existing) return existing;
    const computed = computeInitialSplitState();
    initialSplitStateForMountRef.current = computed;
    return computed;
  })();

  const [amountField, setAmountField] = useState<number>(txn.amount);
  const [isAmountManual, setIsAmountManual] = useState(false);
  const [newSplitState, setNewSplitState] = useState<RecordOf<SplitProps>>(() => initialSplitStateForMount);

  const [leftToSplit, setLeftToSplit] = useState(() => createLeftToSplit(initialSplitStateForMount, txn.amount));
  const [showError, setShowError] = useState(false);
  const [showOneSplitError, setShowOneSplitError] = useState(false);
  const [showLeftToSplitError, setShowLeftToSplitError] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | false>(false);

  const incomeCOAs = useSelector(categoriesSelectors.getIncomeCOAIds);
  const isIncome = transactionsUtils.isIncomeFromTxn(txn, incomeCOAs);

  const showAmountField = isSeriesMode || txn.amount !== 0;
  const isTxnNot0Amount = txn.amount !== 0;
  const splitsTotal = isSeriesMode ? sumNonOverflowSplitItems(newSplitState) : leftToSplit;
  const headerAmount = isTxnNot0Amount ? leftToSplit : splitsTotal;
  const isExistingTxn = txn.id !== null;
  // If the dialog starts with a 0 series amount, keep the series amount row hidden for the whole session.
  const [hideSeriesAmountRow] = useState(() => isSeriesMode && txn.amount === 0);

  const rawSplitStateItems: SplitItemProps[] = splitItemsToArray(newSplitState.items ?? []);
  const newSplitStateItems: SplitItemProps[] = splitItemsToArray(normalizeSplits(newSplitState)?.items ?? []);
  const createDefaultVisibleSections = useCallback(
    (): SplitVisibleSections => ({
      clientProject: false,
      tags: false,
      exclusions: false,
      note: false,
    }),
    [],
  );

  const getSplitLinePolicy = useCallback(
    (item: SplitItemProps) =>
      getSplitQbpLinePolicy({
        splitItem: item,
        mode,
        state: appStore.getState(),
        businessEnabled,
      }),
    [appStore, businessEnabled, mode],
  );

  const getEffectiveSplitExclusionState = useCallback(
    (item: SplitItemProps) =>
      transactionsSelectors.getSplitExclusionDisplayState(appStore.getState(), {
        split: item,
        parentIsExcludedFromF2S: txn.isExcludedFromF2S,
        parentIsExcludedFromReports: txn.isExcludedFromReports,
        transactionId: txn.id ?? undefined,
      }),
    [appStore, txn.id, txn.isExcludedFromF2S, txn.isExcludedFromReports],
  );

  const getSplitRowOrnamentType = useCallback(
    (item: SplitItemProps): OrnamentType | 'none' => {
      const state = appStore.getState();
      const baseOrnamentType = getOrnamentType({
        isIncome: item.coa?.type === 'CATEGORY' ? Boolean(categoriesSelectors.isIncomeCat(state, item.coa.id)) : false,
        isExpense:
          item.coa?.type === 'CATEGORY' ? Boolean(categoriesSelectors.isExpenseCat(state, item.coa.id)) : false,
        isUncategorized: !item.coa || item.coa.type === 'UNCATEGORIZED',
        isAssetPayment: arAccountIds.includes(item.coa?.id ?? ''),
        showPaymentDialogLink:
          arAccountIds.includes(item.coa?.id ?? '') &&
          getTxnTransferHasAllocations(state, { transferId: item.transfer?.id || txn.transfer?.id }),
        isLinkedExpense: Boolean(item.linkedItem?.id),
        amount: item.amount,
        businessId: item.businessId,
      });

      return getAllowedSplitOrnamentType(baseOrnamentType, getSplitLinePolicy(item));
    },
    [appStore, arAccountIds, getSplitLinePolicy, txn.transfer?.id],
  );

  // Auto-show by row data only; the "Always include" pref is layered on top in `getEffectiveVisibleSections`.
  const visibilityForSplitRowItem = useCallback(
    (item: SplitItemProps): SplitVisibleSections =>
      usesNewSplitLineUi
        ? (() => {
            const effectiveExclusionState = getEffectiveSplitExclusionState(item);
            // Auto-show Exclusions only when the line actually has (or inherits) an
            // excluded state. We can't rely on `hasExplicitSplitExclusionOverride` here
            // because `normalizeSplits` writes explicit `false` defaults onto every
            // non-transfer split row, which would make the override check always true
            // and render the section for every split even when nothing is excluded.
            // Fall back to the parent txn's flags because normalization also wipes out
            // the nullish-inheritance signal that `effectiveExclusionState` relies on.
            const hasVisibleExclusions =
              Boolean(effectiveExclusionState.isExcludedFromF2S) ||
              Boolean(effectiveExclusionState.isExcludedFromReports) ||
              Boolean(txn?.isExcludedFromF2S) ||
              Boolean(txn?.isExcludedFromReports);

            if (isOverflowSplitItem(item)) {
              return {
                clientProject: false,
                tags: false,
                exclusions: hasVisibleExclusions,
                note: Boolean(item.memo?.trim()),
              };
            }

            const policy = getSplitLinePolicy(item);
            const ornamentType = getSplitRowOrnamentType(item);
            const hasTags = getLength(item.tags) > 0;
            const hasNote = Boolean(item.memo?.trim());

            return {
              clientProject:
                policy.allowsClientProject &&
                (policy.showClientProjectByDefault ||
                  Boolean(item.customerId || item.projectId) ||
                  ornamentType !== 'none'),
              tags: hasTags,
              exclusions: policy.allowsExclusions && (policy.showExclusionsByDefault || hasVisibleExclusions),
              note: hasNote,
            };
          })()
        : createDefaultVisibleSections(),
    [
      createDefaultVisibleSections,
      getEffectiveSplitExclusionState,
      getSplitLinePolicy,
      getSplitRowOrnamentType,
      txn?.isExcludedFromF2S,
      txn?.isExcludedFromReports,
      usesNewSplitLineUi,
    ],
  );

  const [splitSectionVisibility, setSplitSectionVisibility] = useState<SplitVisibleSections[]>(() => {
    const initialItems = splitItemsToArray(
      normalizeSplits(initialSplitStateForMount)?.items ?? initialSplitStateForMount.items ?? [],
    );
    if (!usesNewSplitLineUi) {
      return initialItems.map(() => createDefaultVisibleSections());
    }
    return initialItems.map((item) => visibilityForSplitRowItem(item));
  });
  const [splitSectionTouched, setSplitSectionTouched] = useState<SplitVisibleSections[]>(() => {
    const initialItems = splitItemsToArray(
      normalizeSplits(initialSplitStateForMount)?.items ?? initialSplitStateForMount.items ?? [],
    );
    return initialItems.map(() => createDefaultVisibleSections());
  });
  const [splitMenuAnchor, setSplitMenuAnchor] = useState<{ anchorEl: HTMLElement; index: number } | null>(null);

  const pendingSplitSectionFocusRef = useRef<{
    index: number;
    section: keyof SplitVisibleSections;
  } | null>(null);

  // Tracks a split-row index whose Category field should receive focus on the next render.
  // Set when a new split line is added so the user can immediately choose a category.
  const pendingCategoryFocusIndexRef = useRef<number | null>(null);
  // Tracks a split-row index whose Amount field should receive focus on the next render.
  // Set when "Left to Split" populates/creates a split line so focus lands on the modified field.
  const pendingAmountFocusIndexRef = useRef<number | null>(null);

  useEffect(() => {
    const categoryFocusIndex = pendingCategoryFocusIndexRef.current;
    if (categoryFocusIndex != null) {
      pendingCategoryFocusIndexRef.current = null;
      const input = document.getElementById(`split-cat-${categoryFocusIndex}-input`);
      if (input instanceof HTMLElement) {
        input.focus();
        return;
      }
    }
    const amountFocusIndex = pendingAmountFocusIndexRef.current;
    if (amountFocusIndex != null) {
      pendingAmountFocusIndexRef.current = null;
      const input = document.getElementById(`split-amount-${amountFocusIndex}`);
      if (input instanceof HTMLElement) {
        input.focus();
      }
    }
  }, [newSplitState]);

  useEffect(() => {
    const pending = pendingSplitSectionFocusRef.current;
    if (!pending) return;
    pendingSplitSectionFocusRef.current = null;

    const { index, section } = pending;
    let cancelled = false;
    const rafId = requestAnimationFrame(() => {
      if (cancelled) return;
      scrollSeriesSplitSectionIntoView(index);
      const el = getSplitSectionFocusTarget(index, section);
      if (el) {
        el.focus({ preventScroll: true });
      }
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
    };
  }, [splitSectionVisibility]);

  const scrollToFirstValidationError = (
    items: SplitItemProps[],
    errors: { splitRowError: boolean; oneSplit: boolean; leftToSplitInvalid: boolean },
  ): void => {
    // Defer until after React re-renders the error states so the target elements
    // (helperText, error message) are in the DOM.
    setTimeout(() => {
      const scrollTo = (element: HTMLElement | null): boolean => {
        if (!element) return false;
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (typeof element.focus === 'function') {
          element.focus({ preventScroll: true });
        }
        return true;
      };

      if (errors.splitRowError) {
        for (let i = 0; i < items.length; i += 1) {
          const item = items[i];
          if (!item) continue;
          if (item.coa === null) {
            if (scrollTo(document.getElementById(`split-cat-${i}-input`))) return;
          }
          if (item.amount === 0 && !isOverflowSplitItem(item)) {
            if (scrollTo(document.getElementById(`split-amount-${i}`))) return;
          }
        }
      }
      if (errors.oneSplit) {
        if (scrollTo(document.getElementById('split-one-error'))) return;
      }
      if (errors.leftToSplitInvalid) {
        if (scrollTo(document.getElementById('split-amount'))) return;
      }
    }, 0);
  };

  const verifyValues = () => {
    if (newSplitStateItems) {
      const { hasError, leftToSplitInvalid, oneSplit, splitRowError } = validateSplitDialogState({
        items: newSplitStateItems,
        isTxnNot0Amount,
        leftToSplit,
        mode,
        isOverflowMode,
      });

      setShowLeftToSplitError(leftToSplitInvalid);
      setShowError(splitRowError);
      setShowOneSplitError(oneSplit);

      if (hasError) {
        setMenuAnchor(false);
        scrollToFirstValidationError(newSplitStateItems, { splitRowError, oneSplit, leftToSplitInvalid });
      } else {
        // Always sync the confirmed amount to the parent form. The txn passed in may already
        // carry a pre-seeded amount (e.g. receipt-generated splits), so comparing against
        // txn.amount would skip the update even though the parent form still has the old amount.
        if (isTxnNot0Amount) {
          setAmount(Number(amountField));
        } else if (txn.amount !== splitsTotal) {
          setAmount(splitsTotal);
        }

        if (newSplitStateItems.length === 0) {
          if (txn.split && txn.split.items) {
            const txnSplitItems = List(splitItemsToArrayOverflowLast(txn.split.items));
            const coa = txnSplitItems.get(0)?.coa;
            if (coa) {
              setCoa(coa);
            }
            setTags(txnSplitItems.get(0)?.tags);
            setCustomersAndProjects({
              projectId: txnSplitItems.get(0)?.projectId ?? '',
              customerId: txnSplitItems.get(0)?.customerId ?? '',
              billable: txnSplitItems.get(0)?.isBillable ?? false,
            });
          }
          setSplit(null);
        } else {
          // Display state may inherit parent exclusions while the row itself is still unset.
          // Materialize those inherited values before persisting so the first split save is stable.
          const itemsForPersist = rawSplitStateItems.map((item) => ({
            ...item,
            ...resolveSplitItemExclusions({
              splitItem: item,
              mode,
              phase: 'seed',
              state: appStore.getState(),
              parentExclusions: {
                isExcludedFromF2S: txn.isExcludedFromF2S,
                isExcludedFromReports: txn.isExcludedFromReports,
              },
            }),
          }));

          const splitToPersist = createSplitToPersist({
            split: newSplitState,
            items: itemsForPersist,
            txnCoa: txn.coa,
            mode,
            isOverflowMode,
          });

          setSplit(splitToPersist);
          if (mode === 'transaction' || mode === 'reminder') {
            setCoa(null);
            setTags(List([]));
          }
          setCustomersAndProjects({ projectId: '', customerId: '', billable: false });
        }

        tracker.track(tracker.events.saveSplitsV2, {
          isEditTxn: isExistingTxn,
          hasPositiveSplits: newSplitStateItems.some((splitItem: SplitItemProps) => splitItem.amount >= 0),
          numberOfSplits: newSplitStateItems.length,
        });
        onBack();
      }
    }
  };

  const updateSplitAmount =
    (index: number) =>
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
      const newAmount = Number(e.target.value);
      const currentItem = newSplitStateItems?.[index];
      if (!currentItem) return;
      // Overflow amounts stay locked while the parent txn is a pending scheduled transaction
      // (series template placeholder). Once matched, we let users rebalance the overflow row.
      if (isOverflowSplitItem(currentItem) && !isOverflowAmountEditable(txn)) return;
      if (newAmount !== currentItem.amount) {
        setShowLeftToSplitError(false);
        const nextSplit = newSplitState.setIn(['items', index, 'amount'], newAmount);
        setNewSplitState(nextSplit);

        // Series mode: allow defining splits before entering an amount by deriving the amount from the split lines.
        if (isSeriesMode && !isAmountManual && txn.amount === 0) {
          const derivedAmount = sumNonOverflowSplitItems(nextSplit);
          setAmountField(derivedAmount);
          setLeftToSplit(createLeftToSplit(nextSplit, derivedAmount));
          return;
        }

        if (isTxnNot0Amount) {
          setLeftToSplit(createLeftToSplit(nextSplit, amountField));
        } else {
          setLeftToSplit(leftToSplit + (newAmount - (currentItem.amount || 0)));
        }
      }
    };

  const splitEvenly = () => {
    tracker.track(tracker.events.splitsV2Action, { action: 'splitEvenly' });
    const newEvenlyDividedSplit = splitEvenlyUtil(newSplitState, leftToSplit);
    setNewSplitState(newEvenlyDividedSplit);
    setLeftToSplit(createLeftToSplit(newEvenlyDividedSplit, amountField));
    setMenuAnchor(false);
  };

  const handleCancel = () => {
    onBack();
  };

  const handleDeleteAllSplits = () => {
    // In series mode, splits are an override of the series' main category/tags context,
    // which is left null while a split is active. Restore a valid parent category from the
    // first split row whenever that context is missing (null) or the legacy N/A COA, which
    // QCS rejects.
    const firstNonOverflow = getFirstNonOverflowSplitItem(newSplitStateItems ?? []);
    if (isSeriesMode) {
      if (!txn.coa || isLegacyNACoa(txn.coa)) {
        const nextCoa =
          firstNonOverflow?.coa && !isLegacyNACoa(firstNonOverflow.coa)
            ? firstNonOverflow.coa
            : chartOfAccountsTypes.UNCATEGORIZED_COA;
        setCoa(nextCoa);
      }
    } else {
      if (firstNonOverflow?.coa) {
        setCoa(firstNonOverflow.coa);
      }
      setTags(firstNonOverflow?.tags ?? List([]));
      setCustomersAndProjects({
        projectId: firstNonOverflow?.projectId ?? '',
        customerId: firstNonOverflow?.customerId ?? '',
        billable: firstNonOverflow?.isBillable ?? false,
      });
    }

    setSplit(null);
    tracker.track(tracker.events.splitsV2Action, { action: 'deleteAllSplits' });
    onBack();
  };

  // createLeftToSplitLine either fills an existing empty (amount=0) non-overflow line in-place,
  // or inserts a new line. This returns the index of the row whose amount was actually changed
  // so we can move focus to that field.
  const findAmountModifiedIndex = (currentItems: SplitItemProps[], nextItems: SplitItemProps[]): number | null => {
    for (let i = 0; i < nextItems.length; i += 1) {
      const prevAmount = Number(currentItems[i]?.amount ?? 0);
      const nextAmount = Number(nextItems[i]?.amount ?? 0);
      if (prevAmount !== nextAmount) {
        return i;
      }
    }
    return null;
  };

  // Keeps the per-row visibility/touched arrays aligned with `newSplitState.items` after an insert.
  const insertSplitSectionRow = useCallback(
    (insertedIndex: number, insertedItem: SplitItemProps | undefined): void => {
      const visibility = insertedItem ? visibilityForSplitRowItem(insertedItem) : createDefaultVisibleSections();
      setSplitSectionVisibility((current) => {
        const next = [...current];
        next.splice(insertedIndex, 0, visibility);
        return next;
      });
      setSplitSectionTouched((current) => {
        const next = [...current];
        next.splice(insertedIndex, 0, createDefaultVisibleSections());
        return next;
      });
    },
    [createDefaultVisibleSections, visibilityForSplitRowItem],
  );

  const addLeftToSplitLine = () => {
    tracker.track(tracker.events.splitsV2Action, { action: 'addLeftToSplit' });
    if (Math.round(Math.abs(leftToSplit) * 100) === 0) return;
    setShowOneSplitError(false);

    const baseSplit = createLeftToSplitLine(newSplitState, leftToSplit);
    const nextSplit = seedSplitState(isOverflowMode ? ensureOverflowSplitIsLast(baseSplit) : baseSplit);
    const nextItems = splitItemsToArray(normalizeSplits(nextSplit)?.items ?? nextSplit.items ?? []);
    const insertedIndex = getInsertedEditableSplitIndex(newSplitStateItems, nextItems);

    setNewSplitState(nextSplit);
    if (insertedIndex != null) {
      insertSplitSectionRow(insertedIndex, nextItems[insertedIndex]);
    }

    const modifiedIndex = insertedIndex ?? findAmountModifiedIndex(newSplitStateItems, nextItems);
    if (modifiedIndex != null) {
      pendingAmountFocusIndexRef.current = modifiedIndex;
    }

    // Overflow mode reflows remaining cents into the overflow row; non-overflow consumes everything.
    setLeftToSplit(isOverflowMode ? createLeftToSplit(nextSplit, amountField) : 0);
  };

  const addSplitLine = () => {
    const nextSplit = seedSplitState(addEmptySplitLine(newSplitState, isOverflowMode));
    const nextItems = splitItemsToArray(normalizeSplits(nextSplit)?.items ?? nextSplit.items ?? []);
    const insertedIndex = getInsertedEditableSplitIndex(newSplitStateItems, nextItems);
    setNewSplitState(nextSplit);
    if (insertedIndex != null) {
      insertSplitSectionRow(insertedIndex, nextItems[insertedIndex]);
      pendingCategoryFocusIndexRef.current = insertedIndex;
    }
  };

  const removeSplit = (index: number) => () => {
    const currentItem = newSplitStateItems?.[index];
    if (!currentItem) return;

    const isOverflow = isOverflowSplitItem(currentItem);
    // Series overflow is a permanent template placeholder — never deletable.
    // In any other mode (reminder, transaction) it can be deleted once the txn is no longer pending.
    if (isOverflow && (isSeriesMode || !isOverflowAmountEditable(txn))) return;

    if (!isOverflow) {
      if (isSeriesMode) {
        const nonOverflowCount =
          newSplitStateItems?.filter((item: SplitItemProps) => !isOverflowSplitItem(item)).length ?? 0;
        if (nonOverflowCount <= 1) return;
      } else if (mode === 'reminder') {
        const nonOverflowCount =
          newSplitStateItems?.filter((item: SplitItemProps) => !isOverflowSplitItem(item)).length ?? 0;
        if (nonOverflowCount <= 2) return;
      } else if ((newSplitStateItems?.length ?? 0) <= 1) {
        return;
      }
    }

    tracker.track(tracker.events.splitsV2Action, { action: 'removeSplit' });
    setShowOneSplitError(false);
    if (!isTxnNot0Amount) {
      setLeftToSplit(leftToSplit - (newSplitStateItems?.[index]?.amount || 0));
    } else {
      setLeftToSplit(createLeftToSplit(newSplitState, amountField) + (newSplitStateItems?.[index]?.amount || 0));
    }

    const nextSplit = newSplitState.deleteIn(['items', index]);
    setNewSplitState(nextSplit);
    setSplitSectionVisibility((current) => current.filter((_, currentIndex) => currentIndex !== index));
    setSplitSectionTouched((current) => current.filter((_, currentIndex) => currentIndex !== index));

    if (isSeriesMode && !isAmountManual && txn.amount === 0) {
      const derivedAmount = sumNonOverflowSplitItems(nextSplit);
      setAmountField(derivedAmount);
      setLeftToSplit(createLeftToSplit(nextSplit, derivedAmount));
    }
  };

  const buttonClick = (e: MouseEvent<HTMLElement>): void => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
  };

  const handleSplitOptionsClick =
    (index: number) =>
    (e: MouseEvent<HTMLElement>): void => {
      e.stopPropagation();
      setSplitMenuAnchor({ anchorEl: e.currentTarget, index });
    };

  // Visibility layers: auto-by-data, then `splitsAlwaysShow` for untouched rows,
  // then explicit per-row toggles. Policy gates trump all.
  const getEffectiveVisibleSections = useCallback(
    (
      item: SplitItemProps,
      rawSections: SplitVisibleSections,
      touchedSections: SplitVisibleSections,
      policy: SplitQbpLinePolicy,
    ): SplitVisibleSections => {
      const autoSections = visibilityForSplitRowItem(item);

      return {
        clientProject:
          policy.allowsClientProject &&
          (touchedSections.clientProject ? rawSections.clientProject : autoSections.clientProject),
        tags: touchedSections.tags ? rawSections.tags : splitsAlwaysShow.tags || autoSections.tags,
        exclusions:
          policy.allowsExclusions &&
          (touchedSections.exclusions
            ? rawSections.exclusions
            : splitsAlwaysShow.exclusions || autoSections.exclusions),
        note: touchedSections.note ? rawSections.note : splitsAlwaysShow.note || autoSections.note,
      };
    },
    [splitsAlwaysShow, visibilityForSplitRowItem],
  );

  const toggleSplitSection = (index: number, section: keyof SplitVisibleSections, isVisible: boolean) => (): void => {
    const nextVisible = !isVisible;

    setSplitSectionVisibility((current) => {
      const next = [...current];
      const currentSections = next[index] ?? createDefaultVisibleSections();
      if (nextVisible) {
        pendingSplitSectionFocusRef.current = {
          index,
          section,
        };
      }
      next[index] = {
        ...currentSections,
        [section]: nextVisible,
      };
      return next;
    });
    setSplitSectionTouched((current) => {
      const next = [...current];
      const currentSections = next[index] ?? createDefaultVisibleSections();
      next[index] = {
        ...currentSections,
        [section]: true,
      };
      return next;
    });
    setSplitMenuAnchor(null);
  };

  const toggleSplitsAlwaysShow =
    (key: keyof SplitFieldDefaults) =>
    (event: MouseEvent<HTMLElement>): void => {
      event.stopPropagation();
      commitSplitsAlwaysShow({
        ...splitsAlwaysShow,
        [key]: !splitsAlwaysShow[key],
      });
    };

  const showSplitSection = useCallback(
    (index: number, section: keyof SplitVisibleSections): void => {
      setSplitSectionVisibility((current) => {
        const next = [...current];
        const currentSections = next[index] ?? createDefaultVisibleSections();
        if (currentSections[section]) {
          return current;
        }
        pendingSplitSectionFocusRef.current = {
          index,
          section,
        };
        next[index] = {
          ...currentSections,
          [section]: true,
        };
        return next;
      });
      setSplitSectionTouched((current) => {
        const next = [...current];
        const currentSections = next[index] ?? createDefaultVisibleSections();
        next[index] = {
          ...currentSections,
          [section]: true,
        };
        return next;
      });
    },
    [createDefaultVisibleSections],
  );

  const currentSplitPayments = useAppSelector((state) => getTotalNumberOfSplitsWithAllocations(state, { txn }));
  const isAssetPayment = useAppSelector((state) => {
    const arAccountIds = accountsSelectors.getAllArAccountIds(state);
    return isArAssociatedPaymentTxn(txn, arAccountIds);
  });
  const disabledQBP =
    (businessEnabled && currentSplitPayments > 0) ||
    (txn.type === 'PAYMENT' && !!isAssetPayment) ||
    txn.split?.items?.some((item) => !!item.linkedItem?.id);
  const splitMenuLinePolicy =
    splitMenuAnchor && newSplitStateItems?.[splitMenuAnchor.index]
      ? getSplitLinePolicy(newSplitStateItems[splitMenuAnchor.index])
      : null;
  const menuSx = getStandardDrawerFloatingSx();
  const drawerLayerContainer = useStandardDrawerLayerContainer();

  const privacyModeMask = usePrivacyMaskValue();
  const isLeftToSplitZero = Math.round(Math.abs(leftToSplit) * 100) === 0;

  const renderSplitDialogHeader = ({ mode, payee }: SplitDialogHeaderProps): React.ReactNode => {
    if (mode === 'series' || mode === 'reminder') {
      return (
        <Box className={classes.headerText}>
          {payee ? (
            <Box display={'inline-flex'} gap={0.5}>
              <Typography variant='bodyRegular'>{mode === 'series' ? 'Series:' : 'Reminder:'}</Typography>
              <Typography variant='bodyMedium'>{payee}</Typography>
            </Box>
          ) : null}
          <Typography variant='paragraphRegular' color='textSecondary'>
            {mode === 'series'
              ? "When transactions are matched to this Series, they'll be split in the same way."
              : 'Any changes to your Splits will apply to this Reminder only.'}
          </Typography>
        </Box>
      );
    }

    return (
      <Typography variant='bodyMedium' className={classes.headerText}>
        {payee ? payee : ''}
      </Typography>
    );
  };

  return (
    <SplitDialogShell
      dialogProps={dialogProps}
      isDrawer={Boolean(rootModalDrawer)}
      onBack={onBack}
      onClose={handleCancel}
      open={open}
      paperClassName={classes.splitDialogPlaceholderScope}
      title={title ?? defaultTitle}
    >
      <StdDialogContent
        id='split-transaction-title'
        className={classes.amountSection}
        sx={{
          flex: '0 0 auto',
          minHeight: 0,
        }}
      >
        {renderSplitDialogHeader({ mode, payee: txn.payee })}
        <div className={classes.headerData}>
          {showAmountField ? (
            <div className={isSeriesMode ? `${classes.amountDiv} ${classes.amountDivSeries}` : classes.amountDiv}>
              <div className={isSeriesMode ? classes.amountRowSeries : undefined}>
                {hideSeriesAmountRow ? null : (
                  <>
                    <QAmountField
                      label='Amount'
                      className={classes.amountField}
                      size='small'
                      textFieldVariant='filled'
                      editable
                      onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                        setIsAmountManual(true);
                        const nextAmount = Number(e.target.value);
                        setAmountField(nextAmount);
                        setLeftToSplit(createLeftToSplit(newSplitState, nextAmount));
                        setShowLeftToSplitError(false);
                      }}
                      value={amountField}
                      showSign='both'
                      // Only show green for positive values; keep negative values default (black).
                      showColor='positive'
                      expectedSign={isIncome ? 'positive' : 'negative'}
                      inputId='split-amount'
                      disabled={(disabledQBP || isReadOnlyMode) && !isSeriesMode}
                    />

                    {isSeriesMode && (
                      <Tooltip title='This is the recurring amount for the series.' placement='top' arrow>
                        <IconButton
                          id='split-series-amount-info'
                          className={classes.helpButton}
                          aria-label='Series amount info'
                        >
                          <HelpOutlineIcon fontSize='small' />
                        </IconButton>
                      </Tooltip>
                    )}
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className={classes.newTransactionText}>
              <Typography variant='paragraphRegular'>{isExistingTxn ? '' : 'New Transaction'}</Typography>
            </div>
          )}

          <div className={classes.leftToSplitCol}>
            <Typography variant='overlineMedium'>{isTxnNot0Amount ? 'LEFT TO SPLIT' : 'SPLITS TOTAL'}</Typography>
            <div className={classes.leftToSplitDiv}>
              {leftToSplit === 0 && isTxnNot0Amount && <Check className={classes.success} />}
              <Typography className={headerAmount > 0 ? classes.positive : ''} variant='titleMedium'>
                {privacyModeMask(`${headerAmount > 0 ? '+' : ''}$${Math.abs(headerAmount).toFixed(2)}`)}
              </Typography>
            </div>
          </div>

          <div>
            <ButtonBase
              onClick={buttonClick}
              focusRipple
              id={'split-evenly'}
              aria-label='More'
              className={classes.menu}
            >
              <MenuButton />
            </ButtonBase>

            {menuAnchor && (
              <Menu
                id='account-settings-menu'
                anchorEl={menuAnchor}
                container={drawerLayerContainer ?? undefined}
                keepMounted
                open
                onClose={() => setMenuAnchor(false)}
                onClick={stopProp}
                classes={{ paper: classes.menuRoot }}
                sx={menuSx}
              >
                <Typography id='split-menu-left-to-split-label' variant='overlineMedium'>
                  Left to Split
                </Typography>
                <MenuItem
                  key='menu-split'
                  id='menu-split'
                  onClick={splitEvenly}
                  disabled={isLeftToSplitZero || !isTxnNot0Amount || disabledQBP || isReadOnlyMode}
                  aria-describedby='split-menu-left-to-split-label'
                >
                  <ListItemIcon>
                    <DeviceHubIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={<Typography>Divide among Splits</Typography>}
                    secondary={
                      <Typography className={classes.textWrap} variant='paragraphRegular'>
                        Use to split a bill evenly between friends, or distribute leftover tax
                      </Typography>
                    }
                  />
                </MenuItem>
                {splitsAlwaysShowEnabled
                  ? [
                      <Divider key='split-menu-always-include-divider' />,
                      <Typography
                        key='split-menu-always-include-label'
                        id='split-menu-always-include-label'
                        variant='overlineMedium'
                      >
                        Always include:
                      </Typography>,
                      ...SPLITS_ALWAYS_SHOW_MENU_ITEMS.map((item) => {
                        const isSelected = splitsAlwaysShow[item.key];

                        return (
                          <MenuItem
                            key={item.key}
                            id={item.id}
                            role='menuitemcheckbox'
                            aria-checked={isSelected}
                            aria-describedby='split-menu-always-include-label'
                            onClick={toggleSplitsAlwaysShow(item.key)}
                            className={isSelected ? classes.splitOptionsMenuItemSelected : undefined}
                          >
                            <ListItemIcon className={classes.splitOptionsMenuCheck}>
                              {isSelected ? <Check fontSize='small' /> : null}
                            </ListItemIcon>
                            <ListItemText primary={<Typography variant='bodyRegular'>{item.label}</Typography>} />
                          </MenuItem>
                        );
                      }),
                    ]
                  : null}
              </Menu>
            )}
          </div>
        </div>
      </StdDialogContent>

      <StdDialogContent id='split-transaction-splits' className={classes.content}>
        <Box component='div' data-split-scroll>
          <div className={classes.splitRows}>
            {newSplitState?.items?.map((splitItem, index) => {
              const splitName = formatSplitItemName(splitItem.name);
              const effectiveExclusionState = getEffectiveSplitExclusionState(splitItem);
              const ornamentType = getSplitRowOrnamentType(splitItem);
              const splitPolicy = getSplitLinePolicy(splitItem);
              const rawVisibleSections = splitSectionVisibility[index] ?? createDefaultVisibleSections();
              const touchedSections = splitSectionTouched[index] ?? createDefaultVisibleSections();
              const visibleSections = getEffectiveVisibleSections(
                splitItem,
                rawVisibleSections,
                touchedSections,
                splitPolicy,
              );

              return (
                <div
                  key={index}
                  id={`split-series-section-${index}`}
                  className={usesNewSplitLineUi ? classes.seriesSplitSection : undefined}
                >
                  {usesNewSplitLineUi ? (
                    <div className={classes.seriesSplitHeader}>
                      <div className={classes.seriesSplitHeaderLabel}>
                        <Typography variant='bodyMedium' className={classes.seriesSplitHeaderText}>
                          {isOverflowSplitItem(splitItem) ? 'Overflow' : `Split ${index + 1}`}
                        </Typography>
                        {splitName ? (
                          <Typography variant='captionRegular' className={classes.seriesSplitHeaderName}>
                            {splitName}
                          </Typography>
                        ) : null}
                      </div>
                      <div className={classes.seriesSplitHeaderDivider}>
                        <Divider orientation='horizontal' />
                      </div>
                      <div className={classes.seriesSplitHeaderActions}>
                        <IconButton
                          id={`split-options-series-${index}`}
                          aria-label={isOverflowSplitItem(splitItem) ? 'Overflow split options' : 'Split options'}
                          onClick={handleSplitOptionsClick(index)}
                          size='small'
                          className={classes.seriesSplitMore}
                        >
                          <MoreHorizIcon fontSize='small' />
                        </IconButton>
                        {!isOverflowSplitItem(splitItem) || (!isSeriesMode && isOverflowAmountEditable(txn)) ? (
                          <IconButton
                            id={`split-remove-series-${index}`}
                            aria-label='Remove split'
                            onClick={removeSplit(index)}
                            disabled={!removeSplit(index) || disabledQBP || isReadOnlyMode}
                            size='small'
                            className={classes.seriesSplitRemove}
                          >
                            <HighlightOffIcon fontSize='small' />
                          </IconButton>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                  <SplitRow
                    splitItem={splitItem}
                    index={index}
                    setNewSplitState={setNewSplitState}
                    showError={showError}
                    accountId={accountId}
                    removeSplit={removeSplit}
                    updateSplitAmount={updateSplitAmount}
                    key={index}
                    baseTxn={txn}
                    disabledPayment={disabledPaymentFn(splitItem.businessId ?? '')}
                    disabledQBP={disabledQBP || isReadOnlyMode}
                    mode={mode}
                    visibleSections={visibleSections}
                    effectiveExclusionState={effectiveExclusionState}
                    ornamentType={ornamentType}
                    showExclusions={() => showSplitSection(index, 'exclusions')}
                  />
                </div>
              );
            })}
          </div>

          {showOneSplitError ? (
            <Typography
              id='split-one-error'
              aria-live='polite'
              className={classes.splitValidationError}
              color='error'
              role='alert'
              variant='captionRegular'
            >
              You must add at least 2 splits
            </Typography>
          ) : null}
        </Box>

        {splitMenuAnchor ? (
          <Menu
            id='split-options-menu'
            anchorEl={splitMenuAnchor.anchorEl}
            container={drawerLayerContainer ?? undefined}
            open
            onClose={() => setSplitMenuAnchor(null)}
            classes={{ paper: classes.splitOptionsMenuPaper }}
            sx={menuSx}
          >
            {(
              [
                businessEnabled && splitMenuLinePolicy?.allowsClientProject
                  ? { key: 'clientProject', label: 'Client & Project' }
                  : null,
                { key: 'tags', label: SPLITS_ALWAYS_SHOW_FIELD_LABELS.tags },
                splitMenuLinePolicy?.allowsExclusions
                  ? { key: 'exclusions', label: SPLITS_ALWAYS_SHOW_FIELD_LABELS.exclusions }
                  : null,
                { key: 'note', label: SPLITS_ALWAYS_SHOW_FIELD_LABELS.note },
              ].filter(Boolean) as Array<{ key: keyof SplitVisibleSections; label: string }>
            ).map((option) => {
              const item = newSplitStateItems[splitMenuAnchor.index];
              const rawSections = splitSectionVisibility[splitMenuAnchor.index] ?? createDefaultVisibleSections();
              const touchedSections = splitSectionTouched[splitMenuAnchor.index] ?? createDefaultVisibleSections();
              const sections =
                item && splitMenuLinePolicy
                  ? getEffectiveVisibleSections(item, rawSections, touchedSections, splitMenuLinePolicy)
                  : createDefaultVisibleSections();
              const isSelected = sections[option.key];

              return (
                <MenuItem
                  key={option.key}
                  id={`split-options-${option.key}`}
                  onClick={toggleSplitSection(splitMenuAnchor.index, option.key, isSelected)}
                  className={isSelected ? classes.splitOptionsMenuItemSelected : undefined}
                >
                  <ListItemIcon className={classes.splitOptionsMenuCheck}>
                    {isSelected ? <Check fontSize='small' /> : null}
                  </ListItemIcon>
                  <ListItemText primary={<Typography variant='bodyRegular'>{option.label}</Typography>} />
                </MenuItem>
              );
            })}
          </Menu>
        ) : null}
      </StdDialogContent>

      <div className={classes.splitFooter}>
        <div className={classes.bottomButtons}>
          <QButton
            id='split-add-line'
            onClick={addSplitLine}
            className={classes.buttonBorder}
            variant='reverseAdd'
            disabled={disabledQBP || isReadOnlyMode}
          >
            {mode === 'series' ? 'Split' : 'Add Split'}
          </QButton>
          {isTxnNot0Amount && (
            <QButton
              id='split-add-left-to-split'
              onClick={addLeftToSplitLine}
              disabled={
                splitSeriesFf
                  ? isLeftToSplitZero || Boolean(disabledQBP) || isReadOnlyMode
                  : isLeftToSplitZero || isReadOnlyMode
              }
              className={classes.leftToSplitButton}
              variant='reverseAdd'
            >
              {mode === 'series' ? 'Left to split' : 'Add Left To Split'}
            </QButton>
          )}
        </div>
      </div>

      <StdDialogActions
        // Continue only hands the split back to the transaction form, so it stays usable offline.
        connectionBanner='above'
        primary={
          isReadOnlyMode
            ? {
                children: 'Close',
                onClick: handleCancel,
                variant: 'contained',
                id: 'split-close',
              }
            : {
                children: 'Continue',
                onClick: verifyValues,
                variant: 'contained',
                id: 'split-continue',
                showTooltip: showLeftToSplitError,
                tooltipText: showLeftToSplitError ? 'Left To Split must equal $0' : undefined,
              }
        }
        secondary={
          isReadOnlyMode
            ? undefined
            : {
                children: 'Cancel',
                onClick: handleCancel,
                id: 'split-cancel',
                variant: 'text',
                className: classes.cancelButton,
              }
        }
        tertiaryAction={
          isReadOnlyMode
            ? undefined
            : {
                children: 'Delete All Splits',
                onClick: handleDeleteAllSplits,
                disabled: newSplitStateItems?.length === 0 || disabledQBP,
                className: classes.deleteButton,
                id: 'split-delete-all',
              }
        }
      />
    </SplitDialogShell>
  );
};

export default memo(SplitDialog);
